import { useId, useState, type KeyboardEvent } from 'react';
import { X } from '@phosphor-icons/react';
import { cx } from '../../lib/cx';
import { Icon } from '../Icon';

export interface TagInputProps {
  label?: string;
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  /** The 42px field with a 12px radius of the staff dialogs (Figma A04.1 "Add staff"). */
  compact?: boolean;
  /** `filled` is the grey field with white chips (Figma A05.3 hashtags). */
  variant?: 'outlined' | 'filled';
  className?: string;
}

/** Free-text chips: type, press Enter (or comma) to add, Backspace on an empty field removes the last one. */
export function TagInput({
  label,
  value,
  onChange,
  placeholder,
  compact = false,
  variant = 'outlined',
  className,
}: TagInputProps) {
  const id = useId();
  const [draft, setDraft] = useState('');

  const add = () => {
    const tag = draft.trim();
    setDraft('');
    if (tag && !value.some((t) => t.toLowerCase() === tag.toLowerCase())) onChange([...value, tag]);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      add();
    } else if (e.key === 'Backspace' && !draft && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div className={cx('flex flex-col', compact ? 'gap-(--mbm-space-1-5)' : 'gap-(--mbm-space-2)', className)}>
      {label ? (
        <label htmlFor={id} className="text-label-medium text-text-secondary">
          {label}
        </label>
      ) : null}
      <div
        className={cx(
          'flex flex-wrap items-center gap-(--mbm-space-2) border',
          variant === 'filled' ? 'border-transparent bg-surface-tint' : 'border-border-default bg-surface-card',
          compact
            ? 'min-h-(--mbm-size-input-compact) rounded-(--mbm-radius-sm) px-(--mbm-space-3) py-(--mbm-space-1)'
            : 'min-h-(--mbm-size-input) rounded-(--mbm-component-radius-input) px-(--mbm-space-3) py-(--mbm-space-2)',
          'focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-focus-ring',
        )}
      >
        {value.map((tag) => (
          <span
            key={tag}
            className={cx(
              'inline-flex items-center gap-(--mbm-space-1) rounded-full py-(--mbm-space-0-5) pl-(--mbm-space-2-5) pr-(--mbm-space-1-5) text-label-small text-text-primary',
              variant === 'filled' ? 'bg-surface-card' : 'bg-surface-tint',
            )}
          >
            {tag}
            <button
              type="button"
              aria-label={`Remove ${tag}`}
              onClick={() => onChange(value.filter((t) => t !== tag))}
              className="rounded-full text-text-tertiary hover:text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
            >
              <Icon icon={X} size="xs" />
            </button>
          </span>
        ))}
        <input
          id={id}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={add}
          placeholder={value.length === 0 ? placeholder : undefined}
          className="min-w-(--mbm-space-9) flex-1 bg-transparent text-body-medium text-text-primary outline-none placeholder:text-text-muted"
        />
      </div>
    </div>
  );
}
